import { Box, Flex } from "@chakra-ui/react";
import * as React from "react";
import {
  CalendarClock,
  Code2,
  FileCode,
  GitBranch,
  Hash,
  Layers,
  RefreshCw,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { ReportStrategyResponse } from "../../../types/reports";
import {
  masterPineUploadStatusLabel,
  tradingViewPineCreatedStatusLabel,
} from "../../../utils/reportStrategyPineUi";
import {
  StrategyTableMasterPineBadge,
  StrategyTableTradingViewPineBadge,
} from "./strategyTablePineBadges";
import {
  REPORTS_SYMBOL_BADGE_CLASS,
  ReportsCardMetaItems,
  ReportsMetaIconChip,
  reportDetailSectionCardProps,
} from "../Reports/components/ReportsCardChrome";

export { reportDetailSectionCardProps as strategyDetailHeaderCardProps };

export function buildStrategyDetailMetaItems(options: {
  strategyId: string;
  policyVersionCount: number;
  activePolicyVersionCount: number;
  archivedPolicyVersionCount: number;
  currentVersionLabel?: string | null;
  strategy: ReportStrategyResponse;
  createdAtDisplay?: string | null;
  updatedAtDisplay?: string | null;
}): Array<{ id: string; node: React.ReactNode }> {
  const items: Array<{ id: string; node: React.ReactNode }> = [];

  const policyValue =
    options.archivedPolicyVersionCount > 0
      ? `${options.policyVersionCount} total · ${options.activePolicyVersionCount} active`
      : String(options.activePolicyVersionCount);

  items.push({
    id: "policies",
    node: (
      <ReportsMetaIconChip
        id="policies"
        label="Policy versions"
        value={policyValue}
        icon={Layers}
      />
    ),
  });

  if (options.currentVersionLabel) {
    items.push({
      id: "current-version",
      node: (
        <ReportsMetaIconChip
          id="current-version"
          label="Current"
          value={options.currentVersionLabel}
          icon={GitBranch}
        />
      ),
    });
  }

  items.push({
    id: "master-pine",
    node: (
      <ReportsMetaIconChip
        id="master-pine"
        label="Master Pinescript"
        value={masterPineUploadStatusLabel(options.strategy)}
        icon={FileCode}
        valueNode={
          <span data-strategy-pine-overview="">
            <StrategyTableMasterPineBadge strategy={options.strategy} />
          </span>
        }
      />
    ),
  });

  items.push({
    id: "compose",
    node: (
      <ReportsMetaIconChip
        id="compose"
        label="TradingView Pinescript"
        value={tradingViewPineCreatedStatusLabel(options.strategy)}
        icon={Code2}
        valueNode={
          <span data-strategy-pine-overview="">
            <StrategyTableTradingViewPineBadge strategy={options.strategy} />
          </span>
        }
      />
    ),
  });

  if (options.createdAtDisplay) {
    items.push({
      id: "created",
      node: (
        <ReportsMetaIconChip
          id="created"
          label="Created"
          value={options.createdAtDisplay}
          icon={CalendarClock}
        />
      ),
    });
  }

  if (options.updatedAtDisplay) {
    items.push({
      id: "updated",
      node: (
        <ReportsMetaIconChip
          id="updated"
          label="Updated"
          value={options.updatedAtDisplay}
          icon={RefreshCw}
        />
      ),
    });
  }

  items.push({
    id: "strategy-id",
    node: (
      <ReportsMetaIconChip
        id="strategy-id"
        label="ID"
        value={options.strategyId}
        icon={Hash}
      />
    ),
  });

  return items;
}

export function StrategyDetailHeaderBadges({
  strategy,
}: {
  strategy: ReportStrategyResponse;
}): React.ReactElement {
  const fillCount =
    typeof strategy.research_sim_fill_count === "number"
      ? Math.round(strategy.research_sim_fill_count)
      : null;
  return (
    <Flex
      gap={2}
      flexShrink={0}
      align="center"
      justify="flex-end"
      flexWrap="wrap"
      data-reports-card-header-badges=""
    >
      {strategy.has_research_sim ? (
        <Badge
          variant="outline"
          data-reports-symbol-badge=""
          data-strategy-research-sim-badge=""
          className={REPORTS_SYMBOL_BADGE_CLASS}
        >
          {fillCount != null && fillCount > 0
            ? `Research sim · ${fillCount} fills`
            : "Research sim"}
        </Badge>
      ) : null}
      {strategy.is_archived ? (
        <Badge variant="outline" data-reports-symbol-badge="" className={REPORTS_SYMBOL_BADGE_CLASS}>
          Archived
        </Badge>
      ) : null}
    </Flex>
  );
}

/** Meta row + status badges — same icon-chip pattern as report/simulation detail. */
export function StrategyCardMetaRowWithBadges({
  metaItems,
  strategy,
}: {
  metaItems: Array<{ id: string; node: React.ReactNode }>;
  strategy: ReportStrategyResponse;
}): React.ReactElement | null {
  if (metaItems.length === 0) return null;

  return (
    <Flex align="center" justify="space-between" gap={3} mt={2} minW={0} w="100%" flexShrink={0}>
      <Box flex="1" minW={0} overflow="hidden" mr={2}>
        <ReportsCardMetaItems items={metaItems} />
      </Box>
      <StrategyDetailHeaderBadges strategy={strategy} />
    </Flex>
  );
}
